
import util from '../lib/util';
import * as DateUtil from '../lib/dateutils';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Lead
 *
 * A prospective-client intake record that is created when a referral is made or a marketing response (call, email, etc.) is received. It tracks the potential client(s), associated case types, initial consult scheduling, retainer amounts requested and received, and estimated case value. A lead can be converted into one or more cases (matters) when the firm takes on the client; it may also be won, lost, refused, or left waiting.
 */
class Lead extends Base implements EntityType {
    leadId: uuid;
    name: string;
    clients: uuid[];
    closeDate?: Date | null;
    campaigns: uuid[];
    caseTypes: uuid[];
    payers: uuid[];
    estimatedValue?: number | null;
    initialConsultDate?: Date | null;
    referrers: uuid[];
    retainerRequested?: number | null;
    retainerReceived?: number | null;
    paidConsult?: string | null;

    /**
     * Create a new Lead, with an (optional) JSON object or Lead class
     *
     * @param {Object|Lead} doc - Can be either a simple JSON object or a Lead class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Lead | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            leadId: genUUID(),
            name: null,
            clients: [],
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'lead';
        // @ts-ignore
        this.leadId = doc._leadId || doc.leadId || doc.leadid || genUUID();
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.clients = doc._clients || doc.clients || [];
        // @ts-ignore
        this.closeDate = DateUtil.timestampOnly(doc._closeDate) || DateUtil.timestampOnly(doc.closeDate) || DateUtil.timestampOnly(doc.closedate) || null;
        // @ts-ignore
        this.campaigns = doc._campaigns || doc.campaigns || [];
        // @ts-ignore
        this.caseTypes = doc._caseTypes || doc.caseTypes || doc.casetypes || [];
        // @ts-ignore
        this.payers = doc._payers || doc.payers || [];
        // @ts-ignore
        this.estimatedValue = util.coalesce(util.parseFloat(doc._estimatedValue), util.parseFloat(doc.estimatedValue), util.parseFloat(doc.estimatedvalue), null);
        // @ts-ignore
        this.initialConsultDate = DateUtil.timestampOnly(doc._initialConsultDate) || DateUtil.timestampOnly(doc.initialConsultDate) || DateUtil.timestampOnly(doc.initialconsultdate) || null;
        // @ts-ignore
        this.referrers = doc._referrers || doc.referrers || [];
        // @ts-ignore
        this.retainerRequested = util.coalesce(util.parseFloat(doc._retainerRequested), util.parseFloat(doc.retainerRequested), util.parseFloat(doc.retainerrequested), null);
        // @ts-ignore
        this.retainerReceived = util.coalesce(util.parseFloat(doc._retainerReceived), util.parseFloat(doc.retainerReceived), util.parseFloat(doc.retainerreceived), null);
        // @ts-ignore
        this.paidConsult = util.trimRight(doc._paidConsult) || util.trimRight(doc.paidConsult) || util.trimRight(doc.paidconsult) || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the leadId across all entity types
     */
    get id(): uuid {
        return this.leadId;
    }


    /**
     * Add a client to lead.clients
     *
     * @param {uuid} client - The Client to add
     * @returns uuid - The client with any defaulted fields set
     */
    addClient(client: uuid) : uuid {
        if (!client) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.clients) this.clients = [];
        this.clients.push(client);

        return client;
    }

    /**
     * Update a client from lead
     *
     * @param {uuid} client - The Client to remove
     */
    updateClient(client: uuid) {
        if (!this.clients) this.clients = [];
        
        // Find the matching object by client.clientId
        const index = this.clients.findIndex(item => item === client);
        if (index !== -1) {
            this.clients[index] = client;
        }
        else this.addClient(client);
    }

    /**
     * Remove a client from lead
     *
     * @param {uuid} client - The Client to remove
     */
    removeClient(client: uuid) {
        if (!this.clients) return;
        
        // Find the matching object by client.clientId
        let foundItems = this.clients?.filter(item => item === client);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.clients = this.clients.filter(item => !(item === client));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonClients() {
        this.clients = [];
     }
        
    /**
     * Add a campaign to lead.campaigns
     *
     * @param {uuid} campaign - The Campaign to add
     * @returns uuid - The campaign with any defaulted fields set
     */
    addCampaign(campaign: uuid) : uuid {
        if (!campaign) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.campaigns) this.campaigns = [];
        this.campaigns.push(campaign);

        return campaign;
    }

    /**
     * Update a campaign from lead
     *
     * @param {uuid} campaign - The Campaign to remove
     */
    updateCampaign(campaign: uuid) {
        if (!this.campaigns) this.campaigns = [];
        
        // Find the matching object by campaign.campaignId
        const index = this.campaigns.findIndex(item => item === campaign);
        if (index !== -1) {
            this.campaigns[index] = campaign;
        }
        else this.addCampaign(campaign);
    }

    /**
     * Remove a campaign from lead
     *
     * @param {uuid} campaign - The Campaign to remove
     */
    removeCampaign(campaign: uuid) {
        if (!this.campaigns) return;
        
        // Find the matching object by campaign.campaignId
        let foundItems = this.campaigns?.filter(item => item === campaign);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.campaigns = this.campaigns.filter(item => !(item === campaign));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonCampaigns() {
        this.campaigns = [];
     }
        
    /**
     * Add a caseType to lead.caseTypes
     *
     * @param {uuid} caseType - The CaseType to add
     * @returns uuid - The caseType with any defaulted fields set
     */
    addCaseType(caseType: uuid) : uuid {
        if (!caseType) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.caseTypes) this.caseTypes = [];
        this.caseTypes.push(caseType);

        return caseType;
    }

    /**
     * Update a caseType from lead
     *
     * @param {uuid} caseType - The CaseType to remove
     */
    updateCaseType(caseType: uuid) {
        if (!this.caseTypes) this.caseTypes = [];
        
        // Find the matching object by caseType.caseTypeId
        const index = this.caseTypes.findIndex(item => item === caseType);
        if (index !== -1) {
            this.caseTypes[index] = caseType;
        }
        else this.addCaseType(caseType);
    }

    /**
     * Remove a caseType from lead
     *
     * @param {uuid} caseType - The CaseType to remove
     */
    removeCaseType(caseType: uuid) {
        if (!this.caseTypes) return;
        
        // Find the matching object by caseType.caseTypeId
        let foundItems = this.caseTypes?.filter(item => item === caseType);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.caseTypes = this.caseTypes.filter(item => !(item === caseType));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonCaseTypes() {
        this.caseTypes = [];
     }
        
    /**
     * Add a payer to lead.payers
     *
     * @param {uuid} payer - The Payer to add
     * @returns uuid - The payer with any defaulted fields set
     */
    addPayer(payer: uuid) : uuid {
        if (!payer) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.payers) this.payers = [];
        this.payers.push(payer);

        return payer;
    }

    /**
     * Update a payer from lead
     *
     * @param {uuid} payer - The Payer to remove
     */
    updatePayer(payer: uuid) {
        if (!this.payers) this.payers = [];
        
        // Find the matching object by payer.payerId
        const index = this.payers.findIndex(item => item === payer);
        if (index !== -1) {
            this.payers[index] = payer;
        }
        else this.addPayer(payer);
    }

    /**
     * Remove a payer from lead
     *
     * @param {uuid} payer - The Payer to remove
     */
    removePayer(payer: uuid) {
        if (!this.payers) return;
        
        // Find the matching object by payer.payerId
        let foundItems = this.payers?.filter(item => item === payer);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.payers = this.payers.filter(item => !(item === payer));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonPayers() {
        this.payers = [];
     }
        
    /**
     * Add a referrer to lead.referrers
     *
     * @param {uuid} referrer - The Referrer to add
     * @returns uuid - The referrer with any defaulted fields set
     */
    addReferrer(referrer: uuid) : uuid {
        if (!referrer) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.referrers) this.referrers = [];
        this.referrers.push(referrer);

        return referrer;
    }

    /**
     * Update a referrer from lead
     *
     * @param {uuid} referrer - The Referrer to remove
     */
    updateReferrer(referrer: uuid) {
        if (!this.referrers) this.referrers = [];
        
        // Find the matching object by referrer.referrerId
        const index = this.referrers.findIndex(item => item === referrer);
        if (index !== -1) {
            this.referrers[index] = referrer;
        }
        else this.addReferrer(referrer);
    }

    /**
     * Remove a referrer from lead
     *
     * @param {uuid} referrer - The Referrer to remove
     */
    removeReferrer(referrer: uuid) {
        if (!this.referrers) return;
        
        // Find the matching object by referrer.referrerId
        let foundItems = this.referrers?.filter(item => item === referrer);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.referrers = this.referrers.filter(item => !(item === referrer));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonReferrers() {
        this.referrers = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this lead"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.leadId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["clients", "campaigns", "caseTypes", "payers", "referrers"];
    }

    /**
     * Default entries are Outlaw-supplied Lead entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Lead[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'lead',
            table: 'LEAD',
            comment: 'A prospective-client intake record that is created when a referral is made or a marketing response (call, email, etc.) is received. It tracks the potential client(s), associated case types, initial consult scheduling, retainer amounts requested and received, and estimated case value. A lead can be converted into one or more cases (matters) when the firm takes on the client; it may also be won, lost, refused, or left waiting.',
            fields: {
                leadId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this lead'
                },
                // Clients and other contacts related to this case
                // Note that we may not know the name of the referral until they call!
                name: {
                    type: 'string',
                    required: true,
                    comment: `The lead's name`
                },
                clients: {
                    type: 'uuid[]',
                    entityType: 'contact',
                    required: true,
                    comment: 'The clients, empty array means we do not know any names yet'
                },
                closeDate: {
                    type: 'timestamp',
                    comment: 'Estimated close date (sign and fund retention)'
                },
                campaigns: {
                    type: 'uuid[]',
                    entityType: 'campaign',
                    comment: `The marketing campaigns that brought in this lead's client`
                },
                caseTypes: {
                    type: 'uuid[]',
                    entityType: 'caseType',
                    comment: 'What kind of case type(s) are needed?'
                },
                payers: {
                    type: 'uuid[]',
                    entityType: 'contact',
                    comment: 'Who will be paying the bills?'
                },
                estimatedValue: {
                    type: 'money',
                    comment: 'What is the estimated value of this case??'
                },
                initialConsultDate: {
                    type: 'timestamp',
                    comment: 'When is the initial consult set up for?'
                },
                referrers: {
                    type: 'uuid[]',
                    entityType: 'contact',
                    required: false,
                    comment: `Who referred this lead's client to the firm`
                },
                retainerRequested: {
                    type: 'money',
                    comment: 'How much money did we ask for as a retainer?'
                },
                retainerReceived: {
                    type: 'money',
                    comment: 'How much was the client able to provide for the retainer?'
                },
                /**
                 * We need a boolean with a null possible, so the values here are t, f and null.
                 */
                paidConsult: {
                    type: 'char',
                    comment: 'Whether the initial consult for this lead is charged (defaulted from the case type or system setting; editable per lead)'
                },
            },
            externalRefs: [
                'document', 'comment', 'participant', 'redFlag', 'milestone'
            ],
            maps: [{
                    name: 'caseTypeId',
                    keysFn: l => l.caseTypes,
                    presentFn: (t, k) => t.caseTypes.includes(k)
                },
                {
                    name: 'campaignId',
                    keysFn: l => l.campaigns,
                    presentFn: (t, k) => t.campaigns.includes(k)
                },
                {
                    name: 'clients',
                    keysFn: t => t.clients,
                    presentFn: (t, k) => t.clients?.includes(k)
                },
                {
                    name: 'payers',
                    keysFn: t => t.payers,
                    presentFn: (t, k) => t.payers?.includes(k)
                },
                {
                    name: 'referrers',
                    keysFn: t => t.referrers,
                    presentFn: (t, k) => t.referrers?.includes(k)
                }
            ]
        });
    }

}
export default Lead;
